import React, { useEffect, useState } from 'react';
import { digitalProjects } from '../../mockData';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Apple, Smartphone, X, ArrowRight, Globe2, Clock3 } from 'lucide-react';
import { useIntersectionAnimation } from '../../hooks/useIntersectionAnimation';

const DigitalProjects = () => {
  const sectionRef = useIntersectionAnimation('.project-card', 100, 0.1);
  const [selectedProject, setSelectedProject] = useState(null);

  useEffect(() => {
    if (!selectedProject) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setSelectedProject(null);
    };

    document.addEventListener('keydown', handleKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [selectedProject]);

  return (
    <section id="projects" ref={sectionRef} className="py-24 bg-black relative overflow-hidden">
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-white rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-white rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <div className="text-center mb-16 scroll-reveal">
          <h2 className="text-4xl md:text-6xl font-bold mb-6 hover:scale-105 transition-transform duration-500 inline-block">
            Our Projects
          </h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Digital products designed and built by Ripple Digital
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-4xl mx-auto">
          {digitalProjects.map((project) => (
            <button
              key={project.id}
              type="button"
              onClick={() => setSelectedProject(project)}
              className="text-left w-full focus:outline-none focus-visible:ring-2 focus-visible:ring-white/80 rounded-xl"
              aria-label={project.privateProject ? 'View coming soon project' : `Learn more about ${project.title}`}
            >
              <Card className="project-card group relative overflow-hidden bg-gray-900 border-gray-800 hover:border-gray-500 transition-all duration-500 opacity-0 translate-y-10 hover:scale-[1.03] hover:z-10 mx-auto w-full cursor-pointer">
                <div className="relative h-64 overflow-hidden bg-gradient-to-br from-gray-950 via-gray-900 to-black flex items-center justify-center p-8">
                  <img
                    src={project.image}
                    alt={project.privateProject ? 'Coming soon project preview' : `${project.title} project image`}
                    className={project.imageMode === 'cover'
                      ? 'absolute inset-0 w-full h-full object-cover transition-all duration-500 group-hover:scale-105'
                      : 'w-40 h-40 md:w-44 md:h-44 object-contain rounded-[2rem] shadow-2xl transition-all duration-500 group-hover:scale-110'}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />

                  <Badge className="absolute top-4 right-4 bg-white text-black group-hover:scale-105 transition-transform">
                    {project.status}
                  </Badge>
                </div>

                <div className="p-6 relative">
                  {project.privateProject ? (
                    <div className="min-h-[92px] flex items-center justify-center">
                      <p className="text-xl font-semibold tracking-wide text-white">Coming Soon</p>
                    </div>
                  ) : (
                    <>
                      <p className="text-sm text-gray-400 mb-2">{project.category}</p>
                      <h3 className="text-2xl font-bold mb-3 text-white">
                        {project.title}
                      </h3>
                      <p className="text-gray-400 leading-relaxed mb-5">
                        {project.description}
                      </p>
                      <span className="inline-flex items-center gap-2 text-sm font-semibold text-white group-hover:gap-3 transition-all">
                        Learn more <ArrowRight className="w-4 h-4" />
                      </span>
                    </>
                  )}
                </div>

                <div className="absolute bottom-0 left-0 w-0 h-1 bg-gradient-to-r from-white via-gray-300 to-white transition-all duration-500 group-hover:w-full" />
              </Card>
            </button>
          ))}
        </div>
      </div>

      {selectedProject && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 bg-black/80 backdrop-blur-md"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setSelectedProject(null);
          }}
          role="presentation"
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="project-dialog-title"
            className="relative w-full max-w-5xl max-h-[90vh] overflow-y-auto rounded-2xl border border-white/10 bg-gray-950 shadow-2xl"
          >
            <button
              type="button"
              onClick={() => setSelectedProject(null)}
              className="absolute top-4 right-4 z-20 w-10 h-10 rounded-full bg-black/70 border border-white/10 flex items-center justify-center text-white hover:bg-white hover:text-black transition-colors"
              aria-label="Close project details"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="grid md:grid-cols-[1.25fr_0.75fr]">
              <div className="bg-white flex items-center justify-center p-3 md:p-4 min-h-[420px]">
                <img
                  src={selectedProject.previewImage}
                  alt={selectedProject.privateProject ? 'Coming soon project preview' : `${selectedProject.title} project preview`}
                  className={selectedProject.previewMode === 'wide'
                    ? 'w-full max-w-[760px] max-h-[680px] object-cover rounded-xl shadow-xl'
                    : 'w-full max-w-[290px] max-h-[520px] object-contain rounded-xl'}
                />
              </div>

              <div className="p-7 md:p-10 flex flex-col justify-center">
                {selectedProject.privateProject ? (
                  <div className="flex flex-col items-center md:items-start justify-center min-h-[220px]">
                    <h3 id="project-dialog-title" className="text-3xl md:text-4xl font-bold text-white mb-5">
                      Coming Soon
                    </h3>
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 text-white text-sm font-medium w-fit">
                      <Clock3 className="w-4 h-4" />
                      Coming Soon
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center gap-4 mb-6">
                      <img
                        src={selectedProject.image}
                        alt=""
                        className="w-16 h-16 rounded-2xl object-contain shadow-lg"
                      />
                      <div>
                        <p className="text-sm uppercase tracking-[0.2em] text-violet-400 font-semibold mb-1">
                          {selectedProject.modalLabel || selectedProject.category}
                        </p>
                        <h3 id="project-dialog-title" className="text-3xl font-bold text-white">
                          {selectedProject.title}
                        </h3>
                      </div>
                    </div>

                    <p className="text-gray-300 leading-relaxed mb-7">
                      {selectedProject.details}
                    </p>

                    <p className="text-sm font-semibold text-gray-200 mb-3">{selectedProject.availabilityText}</p>
                    {selectedProject.platforms?.length ? (
                      <div className="flex flex-wrap gap-3">
                        {selectedProject.platforms.includes('iOS') && (
                          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 text-white text-sm font-medium">
                            <Apple className="w-4 h-4" />
                            iOS
                          </div>
                        )}
                        {selectedProject.platforms.includes('Android') && (
                          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 text-white text-sm font-medium">
                            <Smartphone className="w-4 h-4" />
                            Android
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 text-white text-sm font-medium w-fit">
                        {selectedProject.status === 'Coming Soon' ? <Clock3 className="w-4 h-4" /> : <Globe2 className="w-4 h-4" />}
                        {selectedProject.status}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default DigitalProjects;
